"use client";
import Bg from "@/components/bg";
import { createClient } from "@/utils/supabase/client";
import { getUserClearingStale } from "@/utils/supabase/session";
import { Button, Space } from "antd";
import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { ReadOutlined } from "@ant-design/icons";
import type { User } from "@supabase/supabase-js";
import { redirectToFreeeLogin } from "@/lib/auth/freeeLoginUrl";
import { getLoginErrorMessage } from "@/lib/guide/loginError";

const subscribeNothing = () => () => {};

/** ログイン失敗で戻されたときの ?error= (サーバー描画時は null) */
function useLoginErrorCode(): string | null {
  return useSyncExternalStore(
    subscribeNothing,
    () => new URLSearchParams(window.location.search).get("error"),
    () => null,
  );
}

export default function Home() {
  const [user, setUser] = useState<User | null>(null);
  // テストログインボタンの env 条件 (NEXT_PUBLIC_TEST_LOGIN) は、サーバーランタイム (.dev.vars)
  // とクライアントビルド時 (.env) で値がズレるとボタン有無が SSR/CSR で食い違い、
  // React #418 ハイドレーションミスマッチを起こす。マウント後のみ描画して SSR に含めない。
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const supabase = createClient();
    // getUser() は refresh token 失効時に throw せず error を返すため、
    // getUserClearingStale が失効 (#125) を検知して signOut で stale トークンを purge する。
    // 一時的なネットワーク失敗 (#126) は throw されるため signOut せず未ログイン扱いに留める。
    getUserClearingStale(supabase)
      .then((u) => setUser(u))
      .catch((e: unknown) => {
        console.warn("[Home] getUser failed (transient):", e);
        setUser(null);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const loginErrorMessage = getLoginErrorMessage(useLoginErrorCode());

  const handleLogin = () => {
    redirectToFreeeLogin();
  };

  const handleLogout = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <div style={{ position: "relative", height: "100vh", overflow: "hidden" }}>
      <Bg />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: "20px",
        }}
      >
        <Space>
          {!user ? (
            <>
              <Button type="primary" size="large" onClick={handleLogin}>
                ログイン
              </Button>
              {mounted && process.env.NEXT_PUBLIC_TEST_LOGIN === "true" && (
                <Button
                  size="large"
                  onClick={() => (window.location.href = "/test-login")}
                >
                  テストログイン
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                type="primary"
                size="large"
                onClick={() => (window.location.href = "/home")}
              >
                ホームページへ
              </Button>
              <Button size="large" onClick={handleLogout}>
                ログアウト
              </Button>
            </>
          )}
        </Space>

        {!user && loginErrorMessage && (
          <div
            role="alert"
            style={{
              maxWidth: 320,
              margin: "0 16px",
              padding: "10px 14px",
              background: "rgba(255,255,255,0.92)",
              border: "1px solid #e6b6b0",
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.7,
              color: "#3a3a3a",
            }}
          >
            {loginErrorMessage}
            <div style={{ marginTop: 4 }}>
              <Link href="/guide#trouble" style={{ color: "#a08a4f" }}>
                ログインできないとき →
              </Link>
            </div>
          </div>
        )}

        <Link
          href="/guide"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.75)",
            border: "1px solid var(--color-border-soft, #d6d6d6)",
            color: "var(--color-text-primary, #3a3a3a)",
            fontSize: 13,
            letterSpacing: "0.04em",
            textDecoration: "none",
          }}
        >
          <ReadOutlined />
          はじめての方へ（使い方ガイド）
        </Link>
      </div>
    </div>
  );
}
